import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

/*
 * Button — VOLL DS v2.
 *
 * Examples:
 *   <Button variant="primary">Save</Button>
 *   <Button variant="outline" size="sm">Cancel</Button>
 *   <Button variant="ghost" size="xs" iconOnly aria-label="More"><Icon/></Button>
 *   <Button variant="danger" onClick={onDelete}>Delete</Button>
 *   <Button asChild><a href="/x">Link styled as button</a></Button>
 *
 * Variant set is locked by the design system. The component does not accept
 * a `className` prop — every visual decision lives in the variant table here.
 * If you need a look that isn't expressed by an existing variant, change the
 * design system first, then update this file.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap border font-medium ' +
    'tracking-display-soft ' +
    'transition-colors duration-fast ease-standard ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    // `aria-disabled` is the turned-off button that must stay reachable:
    // focusable and hoverable, so a tooltip can explain why it is off. It
    // therefore gets the look without the pointer-event lockout its native
    // counterpart above applies.
    'aria-disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent ' +
    'focus-visible:outline-none',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground border-primary ' +
          'hover:bg-primary-hover hover:border-primary-hover active:bg-primary-press',
        outline: 'bg-card text-foreground border-input hover:bg-muted hover:border-border-strong',
        ghost: 'bg-transparent text-foreground border-transparent hover:bg-muted',
        subtle: 'bg-muted text-foreground border-transparent hover:bg-accent',
        danger: 'bg-transparent text-tone-red-fg border-transparent hover:bg-tone-red-tint',
      },
      size: {
        xs: 'h-5.5 px-2 text-xs gap-1 rounded-md',
        sm: 'h-row-h-compact px-2.5 text-meta gap-1.25 rounded-md',
        default: 'h-row-h px-3 text-base gap-1.5 rounded-md',
        lg: 'h-9 px-3.5 text-card-title gap-1.5 rounded-md',
      },
      iconOnly: {
        true: 'px-0',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'xs', class: 'w-5.5' },
      { iconOnly: true, size: 'sm', class: 'w-row-h-compact' },
      { iconOnly: true, size: 'default', class: 'w-row-h' },
      { iconOnly: true, size: 'lg', class: 'w-9' },
    ],
    defaultVariants: {
      variant: 'outline',
      size: 'default',
      iconOnly: false,
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>, ButtonVariantProps {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, iconOnly, asChild = false, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={buttonVariants({ variant, size, iconOnly })}
      {...props}
    />
  );
});
